import { Key, ReactChild, ReactFragment, ReactPortal, useCallback, useEffect, useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  FormControlLabel,
  IconButton,
  RadioGroup,
  FormControl,
  Radio,
  RadioProps,
} from "@material-ui/core";
import { useSelector, useDispatch } from "react-redux";
import { useStyles } from "./style";
import { RootState } from "../../Redux/Reducers";
import { addToCart, fetchCatalogItemById, setVariantModalVisibility } from "../../Redux/Actions";
import { isStoreTypeB2b, mutateVariantItemWithCartData } from "../../Utils/common";
import CloseIcon from "./../../Assets/images/black-close-icon.png";
import { ToastNotifyInfo } from "../Toast";
import { CTAButton } from "..";
import { withStyles } from "@material-ui/styles";
import { green } from "@material-ui/core/colors";
import { CURRENCY_SYMBOL } from "../../Config";
import clsx from "clsx";

const GreenRadio = withStyles({
  root: {
    "&$checked": {
      color: green[500],
    },
  },
})((props: RadioProps) => <Radio color="default" {...props} />);

const AddVariantListModal = (): JSX.Element => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [activeVariant, setActiveVariant] = useState(-1);
  const cartData = useSelector((state: RootState) => state.cartReducer.data);
  const storeData = useSelector((state: RootState) => state.storeReducer.store);
  const {
    variant: { variantModal },
    detailedItem,
    productLoaderId,
  } = useSelector((state: RootState) => state.commonReducer);
  const isB2bStore = isStoreTypeB2b();

  useEffect(() => {
    if (variantModal && detailedItem === null) {
      dispatch(fetchCatalogItemById({ store_id: storeData.store_id, item_id: productLoaderId }, true));
    }

    if (variantModal === false) {
      setActiveVariant(-1);
    }
  }, [variantModal, detailedItem]);

  const onVariantSelect = (selectedInd: any, item: any) => {
    if (item.available) {
      setActiveVariant(selectedInd);
    } else {
      ToastNotifyInfo("Variant is out of stock!");
    }
  };

  const onClose = () => {
    dispatch(setVariantModalVisibility(false));
  };

  const onAddToCartBtnHandle = () => {
    if (activeVariant === -1) {
      return;
    }

    const newCart = mutateVariantItemWithCartData(
      "add",
      detailedItem,
      detailedItem.variants[activeVariant],
      cartData.items,
    );
    dispatch(addToCart({ ...cartData, items: newCart }));
    onClose();
  };

  const returnVariantName = (variant: { variant_name: string; discounted_price: number }) => {
    return (
      <Box className="flex items-center justify-center w-100">
        <Box width="100%" fontSize="15px">
          {variant.variant_name}
        </Box>
        {!isB2bStore ? 
          <Box className="absolute right-1 f6 gray">
            {CURRENCY_SYMBOL}
            {variant.discounted_price}
          </Box>
         : null}
      </Box>
    );
  };

  const variantAvailabilityStatus = useCallback(
    (itemVariant: Record<string, any>) => {
      if (!variantModal || !itemVariant?.managed_inventory) {
        return false;
      }

      const cartDataItems = cartData?.items || [];
      const cartVariantIndexBySpecificItem = cartDataItems?.findIndex(
        (item: { id: number }) => item.id === detailedItem.id,
      );
      let allVariantAdded = false;

      const allSelectedVariantsOfCartItem = cartDataItems[cartVariantIndexBySpecificItem]?.variants_selected || [];

      const specificVariantIndex = allSelectedVariantsOfCartItem?.findIndex(
        (variant: { variant_id: number }) => variant.variant_id === itemVariant.variant_id,
      );

      if (
        cartVariantIndexBySpecificItem !== -1 &&
        specificVariantIndex !== -1 &&
        allSelectedVariantsOfCartItem[specificVariantIndex].quantity ===
          allSelectedVariantsOfCartItem[specificVariantIndex].available_quantity
      ) {
        allVariantAdded = true;
      }

      return allVariantAdded;
    },
    [detailedItem, variantModal, cartData],
  );

  return (
    <Dialog open={variantModal} maxWidth="xs" fullWidth={true} onClose={onClose} className="relative">
      <IconButton
        color="primary"
        aria-label="Close Order Details Pop Up"
        component="span"
        className={classes.closeIcon}
        onClick={onClose}
      >
        <img src={CloseIcon} alt="close icon" loading="lazy" className="img h2" />
      </IconButton>
      <DialogContent>
        <Box component="h1">Choose One</Box>
        <FormControl component="fieldset" className="w-100">
          <RadioGroup value={activeVariant}>
            {detailedItem?.variants.map(
              (
                variant: {
                  id: number;
                  available: any;
                  variant_name: string;
                  discounted_price: number;
                },
                index: number,
              ) => 
                <FormControlLabel
                  style={{ marginLeft: 0, marginRight: 0 }}
                  className={clsx(
                    classes.formControl,
                    `ba w-100 br3 mb3 pv1 b--black-50 ${variant.available ? `o-100` : `o-50`} ${
                      index === activeVariant && `b--green green`
                    }`,
                  )}
                  key={variant.id}
                  color="default"
                  control={<GreenRadio disabled={variantAvailabilityStatus(variant)} />}
                  label={returnVariantName(variant)}
                  onChange={() => onVariantSelect(index, variant)}
                  checked={index === activeVariant}
                  disabled={variantAvailabilityStatus(variant)}
                />
              ,
            )}
          </RadioGroup>
        </FormControl>
        <Box className="mv3">
          <CTAButton
            customClasses="br-pill w-100 flex justify-center pa3"
            text="ADD"
            customStyles={{ opacity: activeVariant === -1 ? 0.5 : 1 }}
            onClick={onAddToCartBtnHandle}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AddVariantListModal;
